/**
 * Fotoğraf seçici — ADR-17 / §12.3.
 *
 * =============================================================================
 * NEDEN "ADRES YAZ" DEĞİL "DOSYA SEÇ"
 * -----------------------------------------------------------------------------
 * Bu alan eskiden bir metin kutusuydu ve personelden fotoğrafın adresini
 * yapıştırması bekleniyordu. Pratikte olan şuydu: fotoğraf bir sohbet
 * uygulamasında ya da kişisel bir bulut hesabındaydı, adres birkaç hafta sonra
 * ölüyordu ve o öğrenci sessizce "fotoğrafsız" hâle geliyordu — KK-537'nin
 * düşük offline tavan uyguladığı gruba giriyordu, kimse fark etmeden.
 *
 * Artık dosya BİZE yükleniyor. Yükleme kişi kaydedilmeden önce tamamlanır ve
 * geriye yalnızca bir kimlik kalır; forma o kimlik gider.
 *
 * =============================================================================
 * RIZA (§12.3)
 * -----------------------------------------------------------------------------
 * Rıza kutusu bu bileşenin İÇİNDE değil, formda durur — çünkü rıza fotoğrafın
 * değil KİŞİNİN bir niteliğidir ve fotoğraf kaldırıldığında da kaydı ilgilendirir.
 * Bileşen yalnızca "fotoğraf seçildi" bilgisini yukarı verir; kuralı form kurar.
 */
import { useRef, useState } from "react";
import { Avatar, Buton } from "@kantin/ui";
import { medyaYukle, useMedyaGoruntusu } from "../../veri/medya.js";

export function FotografSecici({
  ad,
  soyad,
  medyaId,
  eskiAdres,
  degistir,
  kapali,
}: {
  ad: string;
  soyad: string;
  /** Seçili fotoğrafın medya kimliği; null ise fotoğraf yok. */
  medyaId: string | null;
  /** 0015 öncesi kayıtların serbest metin adresi (varsa gösterilir). */
  eskiAdres?: string | null;
  degistir: (medyaId: string | null) => void;
  kapali?: boolean;
}) {
  const girdiRef = useRef<HTMLInputElement>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const goruntu = useMedyaGoruntusu(medyaId, eskiAdres);

  async function secildi(dosya: File | undefined): Promise<void> {
    if (!dosya) return;
    setHata(null);
    setYukleniyor(true);
    try {
      const sonuc = await medyaYukle(dosya, "kisi_foto");
      degistir(sonuc.medya_id);
    } catch (e) {
      // Sunucunun mesajı KULLANICIYA GÖSTERİLİR: "yalnızca JPEG/PNG/WEBP" ya da
      // "en fazla 5 MB" gibi düzeltilebilir bilgiler taşır. Genel bir "hata
      // oluştu" metni personeli aynı dosyayı tekrar denemeye iter.
      setHata(e instanceof Error ? e.message : "Fotoğraf yüklenemedi.");
    } finally {
      setYukleniyor(false);
      // Aynı dosya ikinci kez seçilebilsin diye girdi sıfırlanır; aksi hâlde
      // tarayıcı "değer değişmedi" deyip change olayını hiç üretmez.
      if (girdiRef.current) girdiRef.current.value = "";
    }
  }

  const fotoVar = Boolean(goruntu.url);

  return (
    <div className="flex items-start gap-3">
      <Avatar
        ad={ad || "?"}
        soyad={soyad || "?"}
        fotoUrl={goruntu.url}
        boyut={72}
      />

      <div className="flex min-w-0 flex-col gap-2">
        <p className="m-0 text-sm font-medium text-murekkep">Fotoğraf</p>
        <p className="m-0 text-xs text-murekkep-3">
          Zorunlu değildir. Yoksa baş harf avatarı kullanılır (ADR-17). JPEG,
          PNG veya WEBP; en fazla 5 MB.
        </p>

        <input
          ref={girdiRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => void secildi(e.target.files?.[0])}
        />

        <div className="flex flex-wrap gap-2">
          <Buton
            type="button"
            tur="ikincil"
            onClick={() => girdiRef.current?.click()}
            yukleniyor={yukleniyor}
            disabled={kapali}
          >
            {fotoVar ? "Fotoğrafı değiştir" : "Fotoğraf seç"}
          </Buton>
          {fotoVar && (
            <Buton
              type="button"
              tur="sessiz"
              onClick={() => {
                setHata(null);
                degistir(null);
              }}
              disabled={kapali || yukleniyor}
            >
              Kaldır
            </Buton>
          )}
        </div>

        {hata && (
          <p role="alert" className="m-0 text-xs text-hata">
            {hata}
          </p>
        )}
        {goruntu.hata && !hata && (
          <p role="alert" className="m-0 text-xs text-hata">
            {goruntu.hata}
          </p>
        )}
      </div>
    </div>
  );
}
